import { useMemo } from 'react';
import {
	formatValue,
	type FormatOptions,
	type MarketPrecision,
	type NumericInput,
} from '@velocity-exchange/common/format';
import { BigNum } from '@velocity-exchange/sdk';
import useShowAccountValues from 'src/hooks/useShowAccountValues';

export type NumberDisplayFormat = {
	value: NumericInput;
	options: FormatOptions;
	/** Market precision, for a tick or step digit spec. */
	market?: MarketPrecision;
};

// Only NumberDisplay calls this; the golden scanner covers its JSX sites, not direct hook calls.
export default function useNumberDisplay(params: NumberDisplayFormat) {
	const { value, options, market } = params;

	const showAccountValues = useShowAccountValues();

	// Decimal and the other object inputs have no useful toString, so key them by shape.
	const valueMemoKey =
		value instanceof BigNum
			? `bn:${value.val.toString()}:${value.precision.toString()}`
			: typeof value === 'object' && value !== null
				? JSON.stringify(value, (_, v) =>
						typeof v === 'bigint' ? v.toString() : v
					)
				: String(value);

	const result = useMemo(
		() => formatValue(value, market ? { ...options, market } : options),
		[valueMemoKey, options, market]
	);

	return { result, showAccountValues };
}
